/**
 * Notifications - everything that has happened, in one list
 * ---------------------------------------------------------
 * Unread items have an amber stripe down the left. Clicking one marks it read
 * and takes you to whatever it is about.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Trash2, CheckCheck } from 'lucide-react';

import { notificationApi, badgesChanged } from '../services/api';
import { timeAgo, safeInternalPath } from '../utils/format';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import BackLink from '../components/BackLink';
import { useI18n } from '../hooks/useI18n';

export default function Notifications() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await notificationApi.list(50);
      setNotifications(data.notifications);
      setUnread(data.unread);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const open = async (notification) => {
    if (!notification.isRead) {
      /**
       * Mark it read on the screen FIRST, before the request finishes.
       *
       * Waiting for the server meant the dot stayed on until the page was
       * loaded again - and for a notification with a link, that never
       * happened, because you left the page immediately. It looked as
       * though clicking one did nothing.
       *
       * Safe to be optimistic: the worst case is a notification shown as
       * read that is still unread on the server, which the next load
       * corrects. Nothing is lost either way.
       */
      setNotifications((current) =>
        current.map((n) => (n._id === notification._id ? { ...n, isRead: true } : n))
      );
      try {
        await notificationApi.markRead(notification._id);
        badgesChanged();
      } catch {
        // Not being able to mark it read should not block navigation.
      }
    }
    // Only ever follow a link that points inside this site.
    const target = safeInternalPath(notification.link);
    if (target) navigate(target);
  };

  const markAll = async () => {
    await notificationApi.markAllRead();
    badgesChanged();
    load();
  };

  const remove = async (event, id) => {
    event.stopPropagation();
    await notificationApi.remove(id);
    // Removing an UNREAD one changes the count too.
    badgesChanged();
    load();
  };

  if (loading) return <Loader label={t('common.loadingNotifications')} />;

  return (
    <div className="container section" style={{ maxWidth: 800 }}>
      <BackLink to="/dashboard" label={t('settingsPage.backToDashboard')} />

      <div className="row-between page-header">
        <div>
          <h1>{t('notifications.title')}</h1>
          <p>{unread > 0 ? t('notifications.unread', { count: unread }) : t('notifications.caughtUp')}</p>
        </div>

        {unread > 0 && (
          <button type="button" className="btn btn-outline btn-sm btn-inline" onClick={markAll}>
            <CheckCheck size={16} /> {t('notifications.markAll')}
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {notifications.length === 0 ? (
        <EmptyState
          icon={Bell}
          title={t('notifications.emptyTitle')}
          message={t('notifications.emptyBody')}
          action={
            <Link to="/marketplace" className="btn btn-primary btn-inline">
              {t('nav.browse')}
            </Link>
          }
        />
      ) : (
        <div className="stack" style={{ gap: 'var(--space-xs)' }}>
          {notifications.map((notification) => (
            <div
              key={notification._id}
              className={`notification${notification.isRead ? '' : ' unread'}`}
              onClick={() => open(notification)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') open(notification);
              }}
              role="button"
              tabIndex={0}
              style={{ cursor: notification.link ? 'pointer' : 'default' }}
            >
              {!notification.isRead && <span className="notification-dot" aria-hidden="true" />}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row-between">
                  <strong>{notification.title}</strong>
                  <span className="text-sm text-muted">{timeAgo(notification.createdAt)}</span>
                </div>
                {notification.body && (
                  <p className="text-sm text-muted" style={{ marginTop: 2 }}>
                    {notification.body}
                  </p>
                )}
              </div>

              <button
                type="button"
                className="modal-close"
                onClick={(event) => remove(event, notification._id)}
                aria-label={t('misc.removeNotification')}
                title={t('notifications.remove')}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
